import { useState, type SyntheticEvent } from 'react';
import { useGlobalContext } from '@slc/tools/GlobalContext';
import { ImageVariant, VARIANT_DIMENSIONS } from './contracts';
import { getImageUrl, getImageSrcSet } from './getImageUrl';
// The site logo, shown as a branded placeholder when an opted-in image fails to load. Its cache-busting
// version (and why it only moves forward) is documented where it is defined.
import { SITE_LOGO_SRC } from '../../staticAssets.constants';

type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  variant?: ImageVariant;
  // Absorbed (never forwarded to the DOM); mirrors Next.js's `fill` boolean.
  fill?: boolean;
  priority?: boolean;
  /**
   * When true, a failed image load is replaced with the tenant's site logo on a
   * branded background instead of rendering a broken-image icon. Opt-in — used
   * for article cover/thumbnail images.
   */
  fallbackToLogo?: boolean;
};

// `priority` is a convenience shorthand for eager, high-priority loading;
// otherwise fall back to caller-provided hints, then sensible defaults.
function resolveLoadingProps(
  priority = false,
  props: ImageProps
): Pick<ImageProps, 'loading' | 'fetchPriority' | 'decoding'> {
  return {
    loading: priority ? 'eager' : (props.loading ?? 'lazy'),
    fetchPriority: priority ? 'high' : (props.fetchPriority ?? 'auto'),
    decoding: priority ? 'auto' : 'async',
  };
}

// Auto-inject intrinsic dimensions from the variant to prevent CLS, unless the
// caller supplied explicit width/height.
function resolveDimensions(variant: ImageVariant | undefined, props: ImageProps) {
  const dimensions = variant ? VARIANT_DIMENSIONS[variant] : undefined;
  return {
    width: props.width ?? dimensions?.width,
    height: props.height ?? dimensions?.height,
  };
}

export function Image({ variant, priority, fill: _fill, sizes, fallbackToLogo, ...props }: ImageProps) {
  const ctx = useGlobalContext();
  const [erroredSrc, setErroredSrc] = useState<string | null>(null);

  // Canonical host for tenant-agnostic club logos (master/logos). Configured per-app
  // via VITE_ASSETS_URL; only set where the per-app imageUrl can't serve /img (Streamline).
  const assetsUrl = import.meta.env.VITE_ASSETS_URL;
  const urlProps = { src: props.src, variant, env: ctx.env, imageUrl: ctx.imageUrl, assetsUrl };
  const resolvedSrc = getImageUrl(urlProps);

  // getImageUrl refuses to build a URL when the stored path or the tenant's
  // imageUrl cannot be resolved (SC-1167), so there is nothing to request. Treat
  // that like a load failure up front rather than waiting for an error event that
  // an absent src would never fire.
  const isUnresolved = resolvedSrc === null;

  // Fallback is active only while the currently-resolved src equals the one
  // that errored. Keying on the errored src (rather than a boolean): a re-render
  // with a *different* src clears the fallback and shows the real image, while a
  // re-render with the same known-broken src keeps it (we don't re-request an
  // image we already know fails, avoiding a flicker). An error on the logo
  // itself sets the same value, so React bails out — no render loop.
  const useFallback = Boolean(fallbackToLogo) && (isUnresolved || erroredSrc === resolvedSrc);

  const src = useFallback
    ? getImageUrl({ src: SITE_LOGO_SRC, env: ctx.env, imageUrl: ctx.imageUrl, assetsUrl })
    : resolvedSrc;
  const srcSet = useFallback ? undefined : getImageSrcSet(urlProps);

  const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    setErroredSrc(resolvedSrc);
    props.onError?.(event);
  };

  const className =
    [props.className, useFallback ? 'slc-image-fallback' : undefined].filter(Boolean).join(' ') || undefined;

  const { loading, fetchPriority, decoding } = resolveLoadingProps(priority, props);
  const { width, height } = resolveDimensions(variant, props);

  return (
    <img
      {...props}
      // In fallback mode the image now shows the generic tenant logo, not the
      // article's photo — drop the article-title alt so screen readers don't
      // announce content that isn't there (the title is in the surrounding
      // page text). Defaults to '' when no alt is supplied, as before.
      alt={(useFallback ? '' : props.alt) ?? ''}
      className={className}
      // Omit the attribute entirely when there is no URL: `src=""` resolves to
      // the document URL, so the browser would re-request the whole page as an
      // image (SC-1167).
      src={src === null ? undefined : src}
      srcSet={srcSet}
      sizes={sizes}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding={decoding}
      width={width}
      height={height}
      // Drop caller-supplied crop/cover styles in fallback mode so the logo is
      // not distorted by the original image's crop geometry.
      style={useFallback ? undefined : props.style}
      onError={fallbackToLogo ? handleError : props.onError}
    />
  );
}
